/**
 * The signed-in learner's home.
 *
 * ------------------------------------------------------------------ the prototype ----
 * Section for section, this is `#screen-dashboard` (index.html:5078-5405, page spec
 * `docs/design-reference/mereka-ux-prototype/specs/pages/03-dashboard.md`):
 *
 *   1. the gradient hero      `.dash__hero` — avatar, "Welcome back, <name>", a count of what
 *                             is on the go, and a "browse" CTA                      (:5099)
 *   2. an announcement        `.alert.alert-info` — static copy in the prototype, and NOT
 *                             built here: there is no announcements feed to fill it   (:5113)
 *   3. three summary panels   `.dash__grid` — **Active Learning Paths** | Upcoming deadlines |
 *                             Recent certificates                                    (:5125)
 *   4. the merged library     `.dash__library.mylearning` — ONE grid holding both learning
 *                             paths and courses as one card shape, paths first, behind a chip
 *                             row of All / Learning Paths / Courses / In progress / Completed
 *                                                                                    (:5164)
 *   5. the footer             the shell's, not this file's
 *
 * The two pathway treatments in (3) and (4) are both the prototype's and they do different
 * jobs: the panel is the glance beside the deadlines, the library is the shelf. See
 * `DashboardPaths.tsx` and `MyLearning.tsx`.
 *
 * Colour is entirely tokens (`--brand-gradient`, `--color-primary`, `--color-ink`), so the
 * hero and the accents are whatever the tenant configured. Nothing here is fixed.
 *
 * Data comes from `/api/learner_home/init`, the same endpoint the official learner dashboard
 * uses. It returns enrolments across EVERY academy, so `getMyCourses` narrows them to this
 * tenant — without that, someone enrolled in two academies would see both lists here.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Banner, Icon, Spinner } from '../../shared/ui';
import { loadSession } from '../../shared/lib/session';
import { getMyCourses } from '../../shared/lib/catalogue';
import { listLearningPaths, PATH_SEAM_KEY } from '../../shared/lib/paths';
import { CertificatesPanel, DeadlinesPanel } from './DashboardPanels';
import VerifyAccountNotice from '../VerifyAccountNotice';
import ActivePathsPanel from './DashboardPaths';
import MyLearning from './MyLearning';
import './learner.css';

export default function Dashboard() {
  // `getMyCourses`, not the platform's dashboard endpoint directly: that endpoint is
  // site-scoped and cannot see a course licensed to this academy from another org. See the
  // note on `licensedLearnerCourses` in catalogue.ts.
  const home = useQuery({ queryKey: ['my-courses'], queryFn: getMyCourses });
  // Greeting only, so a slow session lookup must never hold up the courses.
  const session = useQuery({ queryKey: ['session'], queryFn: loadSession, staleTime: 5 * 60_000 });
  const signedIn = session.data?.authenticated === true;

  // THE SAME QUERY KEY AS THE /paths PAGE, on purpose. React Query then serves the browse
  // page from this fetch when the learner clicks through and vice versa — one request, and
  // no chance of the dashboard and the list disagreeing about a path because they fetched a
  // second apart. Both children below read this one result rather than asking again.
  //
  // `enabled` is what keeps it honest about authentication: every read endpoint of the
  // pathways plugin is IsAuthenticated, and this app turns a 401 into a whole-app redirect
  // to /login. The call is not made at all until the session says who this is.
  const paths = useQuery({
    queryKey: PATH_SEAM_KEY(signedIn),
    queryFn: () => listLearningPaths(signedIn),
    enabled: !session.isPending && signedIn,
    retry: false,
  });

  const courses = useMemo(() => home.data ?? [], [home.data]);
  const pathViews = useMemo(() => paths.data ?? [], [paths.data]);

  const inProgress = courses.filter(
    (entry) =>
      entry.enrollment.hasStarted &&
      !entry.courseRun.isArchived &&
      !entry.certificate.isEarned &&
      !entry.gradeData.isPassing,
  ).length;

  const me = session.data?.user;
  const displayName = me?.name || me?.username || '';
  const firstName = displayName.split(' ')[0] ?? '';

  const pathsPending = !session.isPending && signedIn && paths.isPending;

  return (
    <main className="dash">
      {/* ABOVE the hero on purpose. An unverified learner cannot enrol in anything, so
          this is the only action on the page that matters; under a welcome message it
          reads as an aside. Renders nothing once verified, and nothing while unsure. */}
      {me?.username && <VerifyAccountNotice username={me.username} />}
      <section className="dash__hero">
        <span className="dash__avatar" aria-hidden="true">
          {firstName ? firstName.charAt(0).toUpperCase() : <Icon name="person" />}
        </span>
        <div className="dash__hero-text">
          <h1>{firstName ? `Welcome back, ${firstName}` : 'Welcome back'}</h1>
          <p>
            {inProgress > 0
              ? `You have ${inProgress} ${inProgress === 1 ? 'course' : 'courses'} on the go.`
              : 'Pick up where you left off, or find something new.'}
          </p>
        </div>
        <div className="dash__hero-actions">
          <Link className="ui-btn" data-variant="primary" to="/discover">
            <Icon name="explore" />
            Explore courses
          </Link>
        </div>
      </section>

      {/* The prototype's three-panel row. EVERY panel hides itself when it has nothing to
          say, and the row is `auto-fit` rather than three fixed columns so one panel fills
          the width instead of leaving two holes where the other two would have been. A
          learner with no dates, no certificates and no path enrolment gets no row at all —
          which is most learners on a new academy, and is the correct answer for them. */}
      <div className="dash__grid">
        <ActivePathsPanel
          paths={pathViews}
          pending={pathsPending}
          failed={paths.isError}
          onRetry={() => void paths.refetch()}
        />
        {/* No `courseIds` prop any more, and no `courses.length` guard either: the panel
            asks the server one question scoped by session and Host, so it no longer has to
            wait for the enrolment list to arrive before it can start, and it hides itself
            when the answer is empty. */}
        <DeadlinesPanel />
        <CertificatesPanel />
      </div>

      {home.isLoading ? (
        <Spinner label="Loading your courses…" />
      ) : home.isError ? (
        <Banner tone="danger">
          <strong>We could not load your courses.</strong> Your enrolments are safe — this was
          only the request for them. Reload the page to try again.
        </Banner>
      ) : (
        /* One library, both entities. The paths query is passed in rather than fetched
           again inside it; `pathsPending` is what stops the grid rendering the courses now
           and pushing them down when the paths arrive, since paths sort above them. */
        <MyLearning courses={courses} paths={pathViews} pathsPending={pathsPending} />
      )}
    </main>
  );
}
